import { Component, computed, DestroyRef, effect, EffectRef, ElementRef, inject, OnInit, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { DvmService } from '@services/dvm.service';
import anime from 'animejs';
import { View3dDisplay } from '@models/types/components/View3dDisplay.type';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import { ViewerRouteParamsService } from '@services/viewer-route-params.service';

@Component({
    selector: 'app-fullscreen-toggle',
    imports: [ButtonComponent],
    templateUrl: './fullscreen-toggle.component.html',
    styleUrl: './fullscreen-toggle.component.css'
})
export class FullscreenToggleComponent implements OnInit {

  @ViewChild('fullscreenBtn', { read: ElementRef })
  private readonly fullscreenBtn!: ElementRef<ButtonComponent>;

  // SERVICES
  private readonly dvmService:     DvmService              = inject(DvmService);
  private readonly destroyRef:     DestroyRef              = inject(DestroyRef);
  private readonly routeParams:    ViewerRouteParamsService = inject(ViewerRouteParamsService);

  // CONFIG
  private readonly uiconfig:       Signal<UiConfiguration>  = inject(UiService).uiConfig;
  private readonly view3dDisplay:  Signal<View3dDisplay> = computed((): View3dDisplay=> this.uiconfig().View3dDisplay);

  // GLOBAL STATE
  private readonly view:           Signal<AppViews | null> = this.dvmService.view;

  // BUTTON TYPE
  public readonly type:            ButtonType              = ButtonType.Pill;

  // STATE
  public readonly showElement:     WritableSignal<boolean> = signal(this.routeParams.hasViewId || this.view() === AppViews.Pano);
  public readonly isFullscreen:    Signal<boolean> = this.dvmService.isFullscreen;
  private animationTimer:          ReturnType<typeof setTimeout> | null = null;

  
  // EFFECTS
  private readonly onViewChange:   EffectRef               = effect((): void => {
    
    const view: AppViews | null = this.view(),
          show: boolean = view === AppViews.Pano && this.view3dDisplay().enabled && this.view3dDisplay().fullscreen;

    if(view === null) return;
    
    if (!this.showElement()) {
      this.clearAnimationTimer();
      this.animationTimer = window.setTimeout((): void => {
        this.animateFullscreen(show);
        this.animationTimer = null;
      }, 300);
    } 
    else {
      this.animateFullscreen(show);
    }
    
  })

  // LIFECYCLE HOOKS
  ngOnInit(): void {
    this.destroyRef.onDestroy((): void => this.clearAnimationTimer());
  }

  // METHODS
  public toggleFullscreen(): void {
    
    if(this.view() === AppViews.Pano) {
      this.dvmService.viewer3d.toggleFullscreen();
    }

  }

  private animateFullscreen(show: boolean): void {
    const fullscreenBtn: ButtonComponent | undefined = this.fullscreenBtn?.nativeElement;
    if(!fullscreenBtn) return;

    anime({
      begin:    (): void => {
        if(show && !this.showElement()) this.showElement.set(true);
      },
      complete: (): void => {
        if(!show && this.showElement()) this.showElement.set(false);
      },
      targets:  fullscreenBtn,
      opacity:  show ? 1 : 0,
      duration: 300,
      easing:   'easeInOutQuad',
    })

  }

  private clearAnimationTimer(): void {
    if(!this.animationTimer) return;
    clearTimeout(this.animationTimer);
    this.animationTimer = null;
  }

}
